import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CancelOutlined, CheckCircleOutline, Edit } from '@mui/icons-material';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { adminUsersService, AdminUser } from '@services/admin-users.service.ts';
import { adminResourceUsageService } from '@services/admin-resource-usage.service.ts';
import { PlanChip } from '@components/admin/PlanChip.tsx';
import { ResourceUsageCell } from '@components/admin/ResourceUsageCell.tsx';
import { ResourceUsagePieChart } from '@components/admin/ResourceUsagePieChart.tsx';
import { ResourceUsageAverageCell } from '@components/admin/ResourceUsageAverageCell.tsx';
import { UserStatusChip } from '@components/admin/UserStatusChip.tsx';
import { AdminUserRolesModal, ROLE_LABELS, ROLE_CHIP_COLORS } from '@components/admin/AdminUserRolesModal.tsx';
import { toastService } from '@services/toast.service.ts';

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const toLocalDateStr = (d: Date) =>
  d.toLocaleDateString('uk-UA', { timeZone: userTimeZone });

const isSameDay = (d1: Date, d2: Date) =>
  toLocalDateStr(d1) === toLocalDateStr(d2);

const formatLastLogin = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const time = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', timeZone: userTimeZone });

  if (isSameDay(date, today)) return `Сьогодні, ${time}`;
  if (isSameDay(date, yesterday)) return `Вчора, ${time}`;
  return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: userTimeZone }) + `, ${time}`;
};

const ROLE_OPTIONS = [
  { value: '', label: 'Всі ролі' },
  ...Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label })),
];

const LOCALE_LABELS: Record<string, string> = {
  uk: 'Українська',
  en: 'English',
  ru: 'Русский',
  cs: 'Čeština',
  es: 'Español',
  pl: 'Polski',
  be: 'Беларуская',
};

const AdminAllUsersPageContent: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [rolesModalUser, setRolesModalUser] = useState<AdminUser | null>(null);

  const handleRolesChanged = (userId: number, roles: string[]) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, roles } : u)));
    setRolesModalUser((prev) => (prev && prev.id === userId ? { ...prev, roles } : prev));
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminUsersService.getUsers({
        page,
        limit,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        emailVerified: emailVerifiedFilter ? emailVerifiedFilter === 'true' : undefined,
        status: statusFilter ? Number(statusFilter) : undefined,
      });
      setUsers(result.data);
      setTotal(result.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список користувачів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchQuery, roleFilter, emailVerifiedFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Box data-i-doc={PageIndices.ADMIN_ALL_USERS}>
      <PageHint iDoc={PageIndices.ADMIN_ALL_USERS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-all-users"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем, email або ID',
              value: searchQuery,
              onChange: (v) => { setSearchQuery(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'role',
              type: 'select',
              label: 'Роль',
              value: roleFilter,
              onChange: (v) => { setRoleFilter(v); setPage(1); },
              options: ROLE_OPTIONS,
            },
            {
              id: 'emailVerified',
              type: 'select',
              label: 'Email',
              value: emailVerifiedFilter,
              onChange: (v) => { setEmailVerifiedFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: 'true', label: 'Підтверджено' },
                { value: 'false', label: 'Не підтверджено' },
              ],
            },
            {
              id: 'status',
              type: 'select',
              label: 'Статус',
              value: statusFilter,
              onChange: (v) => { setStatusFilter(v); setPage(1); },
              options: [
                { value: '', label: 'Всі' },
                { value: '1', label: 'Активний' },
                { value: '2', label: 'Заблокований' },
                { value: '3', label: 'Видалений' },
              ],
            },
          ]}
          onReset={() => { setSearchQuery(''); setRoleFilter(''); setEmailVerifiedFilter(''); setStatusFilter(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Користувачів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Аватар</TableCell>
                  <TableCell>Ім'я / Email / Телефон</TableCell>
                  <TableCell>Ролі</TableCell>
                  <TableCell>Ресурси</TableCell>
                  <TableCell>Завантаженість</TableCell>
                  <TableCell>Тариф</TableCell>
                  <TableCell>Мова / Часовий пояс</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Реєстрація / Останній вхід</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <UserAvatar path={user.avatar} name={user.name} sx={{ width: 32, height: 32 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          {user.isEmailVerified ? (
                            <Tooltip title="Email верифіковано">
                              <CheckCircleOutline color="success" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Email не верифіковано">
                              <CancelOutlined color="disabled" sx={{ fontSize: 13 }} />
                            </Tooltip>
                          )}
                        </Box>
                        {user.phone && (
                          <Typography variant="caption" color="text.secondary">{user.phone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            {user.roles.map((role) => (
                              <Chip
                                key={role}
                                label={ROLE_LABELS[role] ?? role}
                                color={ROLE_CHIP_COLORS[role] ?? 'default'}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                          <Tooltip title="Керувати ролями">
                            <IconButton size="small" onClick={() => setRolesModalUser(user)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ResourceUsagePieChart id={user.id} fetchUsage={adminResourceUsageService.getUserResourceUsage} />
                          <ResourceUsageCell id={user.id} fetchUsage={adminResourceUsageService.getUserResourceUsage} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <ResourceUsageAverageCell id={user.id} fetchUsage={adminResourceUsageService.getUserResourceUsage} />
                      </TableCell>
                      <TableCell>
                        <PlanChip subscription={user.subscription} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {user.locale ? (LOCALE_LABELS[user.locale] ?? user.locale) : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {user.timezone ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <UserStatusChip status={user.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{new Date(user.createdAt).toLocaleDateString('uk-UA')}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{formatLastLogin(user.lastLoginAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-all-users"
        />
      </PageCard>

      <AdminUserRolesModal
        open={!!rolesModalUser}
        onClose={() => setRolesModalUser(null)}
        userId={rolesModalUser?.id ?? null}
        userName={rolesModalUser?.name ?? ''}
        roles={rolesModalUser?.roles ?? []}
        onChanged={handleRolesChanged}
      />
    </Box>
  );
};

export const AdminAllUsersPage: React.FC = () => (
  <DashboardLayout>
    <AdminAllUsersPageContent />
  </DashboardLayout>
);
